import React, { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Form, Row, Spin, Tabs } from 'antd';
import { formatRut, validateRut } from '@abstract-cl/rut-ts';
import { useMutation } from '@apollo/client';
import {
  CreateLitePurchaseIntentInput,
  LitePurchaseIntent,
  PurchaseIntentFormValues,
  Insurance,
  CreateInsuranceInput,
  InsuranceUseType,
  Customer,
  CustomerInput,
  PaymentType,
  CarCondition,
} from 'apollo/types';
import operations from 'apollo/operations';
import { VehicleInformation } from 'components/purchaseIntentForm';
import { NextButton } from 'components/ui';
import InsuranceFormInformation from 'components/purchaseIntentForm/insuranceFormInformation';
import { getRoleFromIdToken } from 'utils/getDataFromJwt';
import { trimFormValues } from 'utils/trimFormValues';

const DynamicForm: FC = () => {
  const [segurosForm] = Form.useForm();
  const [tramitesForm] = Form.useForm();
  const [ambosForm] = Form.useForm();

  const history = useHistory();

  const [citDataLoading, setCitDataLoading] = useState(false);
  const [insuranceEnabled, setInsuranceEnabled] = useState(true);
  const [checkedSoapSelection, setCheckedSoapSelection] = useState(true);
  const [checkedTagSelection, setCheckedTagSelection] = useState(true);
  const [checkedVehiclePayTaxes, setCheckedVehiclePayTaxes] = useState(false);
  const [defaultKey, setDefaultKey] = useState('1');
  const [activeTabKey, setActiveTabKey] = useState('1');

  const [
    createLitePurchaseIntent,
    { data: purchaseIntentData, loading: createPurchaseIntentLoading },
  ] = useMutation<
    { createLitePurchaseIntent: LitePurchaseIntent },
    { createLitePurchaseIntentInput: CreateLitePurchaseIntentInput }
  >(operations.CREATE_LITE_PURCHASE_INTENT);

  const [callAnalytics, { loading: analyticLoading }] = useMutation(
    operations.CALL_ANALYTICS,
  );

  const [createCustomer, { data: customerData, loading: customerLoading }] =
    useMutation<{ createCustomer: Customer }, { customerInput: CustomerInput }>(
      operations.POST_PERSONAL_INFORMATION,
    );

  const [
    createInsurance,
    { data: insuranceData, loading: createInsuranceLoading },
  ] = useMutation<
    { createInsurance: Insurance },
    { createInsuranceInput: CreateInsuranceInput }
  >(operations.CREATE_INSURANCE);

  const handleChangeRut = (rut: string): string => {
    if (validateRut(rut)) {
      const formatted: string = formatRut(rut) || '';

      // Actualizar el valor del RUT en todos los formularios
      if (activeTabKey === '1') {
        segurosForm.setFieldsValue({ rut: formatted });
      } else if (activeTabKey === '2') {
        tramitesForm.setFieldsValue({ rut: formatted });
      } else {
        ambosForm.setFieldsValue({ rut: formatted });
      }

      return rut;
    }

    const cleanRut = rut.split('.').join('').split('-').join('');

    // Actualizar el valor del RUT en todos los formularios
    if (activeTabKey === '1') {
      segurosForm.setFieldsValue({ rut: cleanRut });
    } else if (activeTabKey === '2') {
      tramitesForm.setFieldsValue({ rut: cleanRut });
    } else {
      ambosForm.setFieldsValue({ rut: cleanRut });
    }

    return rut;
  };

  const handleShowInsurance = () => {
    const role = getRoleFromIdToken();

    if (role === 'super-admin' || role === 'usuario-seguros') {
      return true;
    }

    return false;
  };

  useEffect(() => {
    const role = getRoleFromIdToken();

    if (role === 'usuario-normal') {
      const newDefaultKey = '2';
      setDefaultKey(newDefaultKey);
      setActiveTabKey(newDefaultKey);
      setInsuranceEnabled(false);
    } else {
      const newDefaultKey = '1';
      setDefaultKey(newDefaultKey);
      setActiveTabKey(newDefaultKey);
      setInsuranceEnabled(true);
    }
  }, []);

  const createCustomerFunction = async (values: any) => {
    await createCustomer({
      variables: {
        customerInput: {
          rut: values.rut || '',
          name: values.insuranceFirstName || '',
          firstSurname: values.insuranceLastName || '',
          commune: '130105',
          phone: values.phone || '',
          email: values.email || '',
        },
      },
    });
  };

  const createInsuranceFunction = async (values: any) => {
    if (insuranceEnabled && activeTabKey !== '2') {
      const carPlate = (values.carPlate || '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .substring(0, 6);

      await createInsurance({
        variables: {
          createInsuranceInput: {
            carCondition: values.carCondition as CarCondition,
            carPlate,
            businessName: values.businessName || undefined,
            rut: values.insuranceRut || values.rut,
            name: values.insuranceFirstName || '',
            firstSurname: values.insuranceLastName || '',
            commune: 'Las Condes',
            communeCode: '130105',
            phone: values.phone || '',
            email: values.email || '',
            username: values.username || '',
            paymentType: values.paymentType || PaymentType.Installments,
            comercialUseType: values.comercialUseType as string,
            insuranceInterest:
              (values.insuranceInterest || '')?.toUpperCase() ||
              'NO HAY INTERES',
            useType: values.insuranceUseType as InsuranceUseType,
          },
        },
      });
    }
  };

  const createLitePurchaseIntentFunction = async (
    values: PurchaseIntentFormValues,
  ) => {
    const { brand, model, year, price, cit, rut, vehicleType, quotationId } =
      values;

    const formattedModel = (model || '').replace(' (ALTO COSTO) ', ' ').trim();
    const createLitePurchaseIntentInput = {
      vehicleType,
      tag: checkedTagSelection,
      obligatoryInsurance: checkedSoapSelection,
      brand,
      model: formattedModel?.split(' * ')[0],
      year,
      quotationId,
      price,
      cit,
      customerRut: rut,
    };

    await createLitePurchaseIntent({
      variables: {
        createLitePurchaseIntentInput,
      },
    });
  };

  const createLitePurchaseIntentEntities = async () => {
    if (purchaseIntentData && purchaseIntentData.createLitePurchaseIntent) {
      localStorage.setItem(
        'purchaseIntentId',
        purchaseIntentData?.createLitePurchaseIntent?.purchaseIntentId || '',
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await createLitePurchaseIntentEntities();
    };
    fetchData();
  }, [purchaseIntentData]);

  // Funciones de finalización específicas para cada formulario
  const onFinishSeguros = async (rawValues: PurchaseIntentFormValues) => {
    const values = trimFormValues(rawValues);
    console.log('onFinishSeguros called', values);
    localStorage.setItem('customerRut', values.rut);
    localStorage.setItem('selectedModel', values.model || '');
    localStorage.removeItem('detailsInViewMode');
    await createLitePurchaseIntentFunction(values);
    await createCustomerFunction(values);
    await createInsuranceFunction(values);
  };

  const onFinishTramites = async (rawValues: PurchaseIntentFormValues) => {
    const values = trimFormValues(rawValues);
    console.log('onFinishTramites called', values);
    localStorage.setItem('customerRut', values.rut);
    localStorage.removeItem('detailsInViewMode');
    await createLitePurchaseIntentFunction(values);
    await createCustomerFunction(values);
  };

  const onFinishAmbos = async (rawValues: PurchaseIntentFormValues) => {
    const values = trimFormValues(rawValues);
    console.log('onFinishAmbos called', values);
    localStorage.setItem('customerRut', values.rut);
    localStorage.setItem('selectedModel', values.model || '');
    localStorage.removeItem('detailsInViewMode');
    await createLitePurchaseIntentFunction(values);
    await createCustomerFunction(values);
    await createInsuranceFunction(values);
  };

  const callAnalyticsFunction = async () => {
    if (customerData && customerData.createCustomer) {
      if (activeTabKey === '1' || activeTabKey === '3') {
        await callAnalytics({
          variables: {
            event: 'Ingreso_tramite_informacion_vehiculo',
          },
        });
        await callAnalytics({
          variables: {
            event: 'evento_vista_seguros',
          },
        });
        history.push({
          pathname: '/insuranceSimulation',
          state: {
            from: history.location,
          },
        });
      } else {
        await callAnalytics({
          variables: {
            event: 'Ingreso_tramite_informacion_vehiculo',
          },
        });
        history.push({
          pathname: '/procedureSelection',
          state: {
            from: history.location,
          },
        });
      }
    }
  };

  useEffect(() => {
    callAnalyticsFunction();
  }, [purchaseIntentData, insuranceData, customerData]);

  const items = [
    {
      key: '1',
      label: 'Seguros',
      content: (
        <div style={{ marginTop: '12px' }}>
          <Spin spinning={citDataLoading}>
            <Form
              form={segurosForm}
              name="segurosForm"
              onFinish={onFinishSeguros}
              scrollToFirstError
            >
              <Row gutter={[0, 12]}>
                <InsuranceFormInformation
                  showInternalQuotationId
                  showCustomerRut
                  insuranceEnabled={insuranceEnabled}
                  setInsuranceEnabled={setInsuranceEnabled}
                  form={segurosForm}
                />
              </Row>
              <NextButton
                htmlType="submit"
                loading={
                  createPurchaseIntentLoading ||
                  analyticLoading ||
                  customerLoading ||
                  createInsuranceLoading
                }
                disabled={
                  !insuranceEnabled ||
                  createPurchaseIntentLoading ||
                  analyticLoading ||
                  customerLoading ||
                  createInsuranceLoading
                }
              />
            </Form>
          </Spin>
        </div>
      ),
    },
    {
      key: '2',
      label: 'Trámites',
      content: (
        <div style={{ marginTop: '12px' }}>
          <Spin spinning={citDataLoading}>
            <Form
              form={tramitesForm}
              name="tramitesForm"
              onFinish={onFinishTramites}
              scrollToFirstError
            >
              <Row gutter={[0, 12]}>
                <VehicleInformation
                  checkedSoapSelection={checkedSoapSelection}
                  setCheckedSoapSelection={setCheckedSoapSelection}
                  form={tramitesForm}
                  handleChangeRut={handleChangeRut}
                  checkedVehiclePayTaxes={checkedVehiclePayTaxes}
                  setCheckedVehiclePayTaxes={setCheckedVehiclePayTaxes}
                  checkedTagSelection={checkedTagSelection}
                  setCheckedTagSelection={setCheckedTagSelection}
                  setCitDataLoading={setCitDataLoading}
                  showVehicleType
                  showVehicleYear
                />
              </Row>
              <NextButton
                htmlType="submit"
                loading={
                  createPurchaseIntentLoading ||
                  analyticLoading ||
                  customerLoading ||
                  createInsuranceLoading
                }
                disabled={
                  createPurchaseIntentLoading ||
                  analyticLoading ||
                  customerLoading ||
                  createInsuranceLoading
                }
              />
            </Form>
          </Spin>
        </div>
      ),
    },
    {
      key: '3',
      label: 'Ambos',
      content: (
        <div style={{ marginTop: '12px' }}>
          <Spin spinning={citDataLoading}>
            <Form
              form={ambosForm}
              name="ambosForm"
              onFinish={onFinishAmbos}
              scrollToFirstError
            >
              <Row gutter={[0, 12]}>
                <VehicleInformation
                  checkedSoapSelection={checkedSoapSelection}
                  setCheckedSoapSelection={setCheckedSoapSelection}
                  form={ambosForm}
                  handleChangeRut={handleChangeRut}
                  checkedVehiclePayTaxes={checkedVehiclePayTaxes}
                  setCheckedVehiclePayTaxes={setCheckedVehiclePayTaxes}
                  checkedTagSelection={checkedTagSelection}
                  setCheckedTagSelection={setCheckedTagSelection}
                  setCitDataLoading={setCitDataLoading}
                  showVehicleType={false}
                  showVehicleYear={false}
                />
                <InsuranceFormInformation
                  showCustomerRut={false}
                  showInternalQuotationId={false}
                  insuranceEnabled={insuranceEnabled}
                  setInsuranceEnabled={setInsuranceEnabled}
                  form={ambosForm}
                />
              </Row>
              <NextButton
                htmlType="submit"
                loading={
                  createPurchaseIntentLoading ||
                  analyticLoading ||
                  customerLoading ||
                  createInsuranceLoading
                }
                disabled={
                  !insuranceEnabled ||
                  createPurchaseIntentLoading ||
                  analyticLoading ||
                  customerLoading ||
                  createInsuranceLoading
                }
              />
            </Form>
          </Spin>
        </div>
      ),
    },
  ];

  const filterItems = items.filter(
    (item) =>
      item.key === '2' ||
      (item.key === '1' && handleShowInsurance()) ||
      (item.key === '3' && handleShowInsurance()),
  );

  return (
    <>
      <Tabs
        style={{ width: 'auto' }}
        activeKey={activeTabKey}
        defaultActiveKey={defaultKey}
        onChange={(key) => {
          setActiveTabKey(key);
          if (key !== '2') {
            setInsuranceEnabled(true);
          } else {
            setInsuranceEnabled(false);
          }
        }}
      >
        {filterItems.map((item) => (
          <Tabs.TabPane key={item.key} tab={item.label}>
            {item.content}
          </Tabs.TabPane>
        ))}
      </Tabs>
    </>
  );
};

export default DynamicForm;
